import React, { createContext, useContext, useState } from 'react';
import { X, Trash2, AlertTriangle, CheckCircle, Info, HelpCircle, LogOut, Lock, Shield, CreditCard, RefreshCw, Play, StopCircle } from 'lucide-react';

const ModalContext = createContext();

export const ModalProvider = ({ children }) => {
    const [confirmModal, setConfirmModal] = useState(null);

    const closeModal = () => setConfirmModal(null);

    const renderModal = () => {
        if (!confirmModal) return null;

        const {
            type,
            title,
            message,
            onConfirm,
            onCancel,
            confirmText = 'Confirmar',
            cancelText = 'Cancelar',
            confirmColor = 'bg-red-600 hover:bg-red-700'
        } = confirmModal;

        // Determinar ícone e cores baseadas no tipo
        let Icon = AlertTriangle;
        let iconBg = 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400';
        let finalConfirmColor = confirmColor;
        if (type === 'delete') {
            Icon = Trash2;
            iconBg = 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
            finalConfirmColor = 'bg-red-600 hover:bg-red-700 shadow-red-600/20';
        } else if (type === 'success' || type === 'completeTrip') {
            Icon = CheckCircle;
            iconBg = 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400';
            finalConfirmColor = 'bg-green-600 hover:bg-green-700 shadow-green-600/20';
        } else if (type === 'info') {
            Icon = Info;
            iconBg = 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400';
            finalConfirmColor = 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20';
        } else if (type === 'logout') {
             Icon = LogOut;
             iconBg = 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400';
             finalConfirmColor = 'bg-red-600 hover:bg-red-700 shadow-red-600/20';
        } else if (type === 'subscription' || type === 'premium' || type === 'locked' || type === 'restoreCloud' || type === 'postShiftAction') {
             Icon = Shield;
             iconBg = 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
             finalConfirmColor = 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20';
        } else if (type === 'fillProvider') {
             Icon = Shield; // Fallback or User icon if available
             iconBg = 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
             finalConfirmColor = 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20';
        } else if (type === 'sync') {
             Icon = RefreshCw;
             iconBg = 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-400';
             finalConfirmColor = 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20';
        } else if (type === 'startTimer' || type === 'startTracker') {
             Icon = Play;
             iconBg = 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400';
             finalConfirmColor = 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20';
        } else if (type === 'stopTimer') {
             Icon = StopCircle;
             iconBg = 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400';
             finalConfirmColor = 'bg-orange-600 hover:bg-orange-700 shadow-orange-600/20';
        }

        return (
            <div className="fixed inset-0 bg-black/60 z-[99999] flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 w-full max-w-sm md:max-w-md lg:max-w-lg shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all scale-100 relative">
                    <button
                        onClick={closeModal}
                        className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                    >
                        <X size={20} />
                    </button>
                    <div className="flex flex-col items-center text-center">
                        <div className={`p-4 rounded-full mb-4 ${iconBg}`}>
                            <Icon size={32} />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{title}</h3>
                        <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm">{message}</p>
                        <div className="flex gap-3 w-full">
                            <button
                                onClick={() => {
                                    if (onCancel) onCancel();
                                    closeModal();
                                }}
                                className="flex-1 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={() => {
                                    if (onConfirm) onConfirm();
                                    closeModal();
                                }}
                                className={`flex-1 py-3 text-white font-bold rounded-xl transition-colors shadow-lg ${finalConfirmColor} dark:shadow-none`}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <ModalContext.Provider value={{ confirmModal, setConfirmModal, closeModal }}>
            {children}
            {renderModal()}
        </ModalContext.Provider>
    );
};

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};
